import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../UserContext';
import PageContainer from '../components/PageContainer';
import { fetchDashboardData } from './dashboard/DashboardUtils';
import Announcements from './dashboard/Announcements';
import SpendingWidget from './dashboard/SpendingWidget';
import '../styles/dashboard.css';

const Dashboard = () => {
  const { userId, canAccessAdminPanel } = useContext(UserContext);

  const [userData, setUserData] = useState({
    business_name: '',
    phone_number: '',
    credit_balance: 0,
    subscription_status: 'trial',
    trial_end_date: null,
    recent_usage: [],
    usage_stats: { total_calls: 0, total_chats: 0, total_duration: 0, total_customers: 0 },
    analysis: {
      daily: { total_interactions: 0, avg_duration: 0, sentiment_score: 0, ai_leads: 0, ai_closed_deals: 0, ai_prospects: 0 },
      weekly: { total_interactions: 0, avg_duration: 0, sentiment_score: 0, ai_leads: 0, ai_closed_deals: 0, ai_prospects: 0 },
      monthly: { total_interactions: 0, avg_duration: 0, sentiment_score: 0, ai_leads: 0, ai_closed_deals: 0, ai_prospects: 0 }
    }
  });

  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setPageLoading(true);
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return;

        const data = await fetchDashboardData(accessToken);
        setUserData(data);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setPageLoading(false);
      }
    };

    if (userId) loadData();
  }, [userId]);

  return (
    <PageContainer
      loading={pageLoading}
      loadingMessage="Loading dashboard..."
      title={`Welcome: ${userData.business_name || ''}`}
      subtitle="Homebase for your AI workforce"
    >
      <div className="dashboard-home">
        {/* Reading order follows importance: announcement, then live metrics,
            then the setup CTAs. The CTAs used to sit in the same 2-col grid as
            the widgets, which gave a onboarding link the same visual weight as
            the spend figures. They are now a compact strip at the bottom. */}
        <div className="dash-layout">
          {/* Announcement spans both columns */}
          <div className="dash-span-2">
            <div className="dash-card dash-announcement-wrap">
              <Announcements phoneNumber={userData.phone_number} />
            </div>
          </div>

          {/* Spending Overview Widget, admins only */}
          {canAccessAdminPanel && (
            <div className="dash-span-2">
              <SpendingWidget creditBalance={userData.credit_balance} />
            </div>
          )}
        </div>

        <section className="dash-getstarted" aria-labelledby="dash-getstarted-title">
          <h2 className="dash-section-title" id="dash-getstarted-title">
            Get started
          </h2>

          <div className="dash-cta-strip">
            <div className="dash-cta">
              <div className="dash-cta-text">
                <div className="dash-cta-title">Quick Setup</div>
                <p className="dash-cta-desc">
                  Follow the guided steps to configure your assistant, connect tools, and start capturing leads.
                </p>
              </div>

              <a
                className="dash-btn dash-btn--primary"
                href="https://nexopta.ai/step-1-welcome/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Set up your AI assistant
              </a>
            </div>

            <div className="dash-cta">
              <div className="dash-cta-text">
                <div className="dash-cta-title">Free Onboarding</div>
                <p className="dash-cta-desc">
                  Book a session to review your workflow, improve conversions, and make sure everything is configured right.
                </p>
              </div>

              <a
                className="dash-btn dash-btn--outline"
                href="https://nexopta.ai/book-now/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a free session
              </a>
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
};

export default Dashboard;
